/**
 * tax_settings_controller.js — Settings > Tax: the registration question, its
 * confirm step, and the sections that follow the answer (Stimulus).
 *
 * The question reads "Are you registered for GST?" / "VAT?" from the chosen
 * region's tax name, with the region's threshold wording (taxNames and
 * thresholdNotes values, owned by REGIONS), so it follows a region change before
 * it is saved.
 *
 * A changed answer is never saved on its own: the confirm target opens with the
 * consequence, and its buttons submit the registration form with update_unsent
 * (1: unsent documents follow the answer, 0: leave them). unsentCount is how
 * many unsent documents charge tax (TaxRegistrationService.count_unsent_with_tax).
 * Cancel puts the saved answer back. The registered-only and unregistered-only
 * sections of the details form follow the chosen answer. Nothing is decided
 * here: the server owns every rule.
 */
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static targets = ["region", "question", "threshold", "yes", "confirm", "confirmText",
                      "confirmApply", "confirmLeave", "save", "registeredOnly", "unregisteredOnly"];
    static values = { taxNames: Object, thresholdNotes: Object, registered: Boolean, unsentCount: Number };

    connect() {
        this.sync();
    }

    sync() {
        const region = this.regionTarget.value;
        this.questionTarget.textContent = `Are you registered for ${this._taxName()}?`;
        const note = this.thresholdNotesValue[region];
        this.thresholdTarget.textContent = note || "";
        this.thresholdTarget.hidden = !note;
        this.choose();
    }

    choose() {
        const registered = this.yesTarget.checked;
        this.registeredOnlyTarget.hidden = !registered;
        this.unregisteredOnlyTarget.hidden = registered;
        const changed = registered !== this.registeredValue;
        this.confirmTarget.hidden = !changed;
        this.saveTarget.hidden = changed;
        if (changed) this._describe(registered);
    }

    cancel() {
        const saved = this.element.querySelector(
            `input[name="tax_registered"][value="${this.registeredValue ? 1 : 0}"]`);
        saved.checked = true;
        this.choose();
    }

    _describe(registered) {
        const tax = this._taxName();
        const count = this.unsentCountValue;
        const removing = !registered && count > 0;
        this.confirmLeaveTarget.hidden = !removing;
        if (removing) {
            const documents = count === 1 ? "1 unsent document includes" : `${count} unsent documents include`;
            this.confirmTextTarget.textContent = `${documents} ${tax}. Remove ${tax} from them?`;
            this.confirmApplyTarget.textContent = `Remove ${tax}`;
        } else if (registered) {
            this.confirmTextTarget.textContent =
                `Your documents will charge ${tax}. Unsent documents priced with ${tax} get it back.`;
            this.confirmApplyTarget.textContent = "Save";
        } else {
            this.confirmTextTarget.textContent = `Your documents will stop charging ${tax}.`;
            this.confirmApplyTarget.textContent = "Save";
        }
    }

    _taxName() {
        return this.taxNamesValue[this.regionTarget.value] || "tax";
    }
}
